import { ref } from 'vue';
import { defineStore } from 'pinia';

/**
 * The existing-user AutoApply flow (EN-776 + EN-777): the modals an account from before the new
 * onboarding walks through on its way to the JobBoard first-run experience. `step` picks the
 * modal the flow host renders; `null` renders nothing.
 *
 * EN-776 owns the entry steps (promo, Resume Gate, 'upload') and calls `startAnalysis()` once the
 * gate resolves. EN-777 owns the rest: 'analysis' -> 'weak-resume' | 'qualification' ->
 * 'preferences' -> full-page handoff to `/job-board/search?firstRun=1`.
 */
export const useExistingUserAutoApplyStore = defineStore('existing-user-auto-apply', () => {
	const step = ref(null);

	// Gate outcome: whether a resume was already on file, and whether one was just uploaded
	// (an upload already runs the parse pipeline, so the analysis must not start another).
	const hadResumeOnFile = ref(false);
	const uploadedNewResume = ref(false);

	// Analysis result, carried into the weak-resume and preferences steps.
	const resumeUuid = ref(null);
	const recommendations = ref(null);

	const startAnalysis = ({ hadResumeOnFile: hadResume = false, uploadedNewResume: uploaded = false } = {}) => {
		hadResumeOnFile.value = hadResume;
		uploadedNewResume.value = uploaded;
		resumeUuid.value = null;
		recommendations.value = null;
		step.value = 'analysis';
	};

	/** `status` is the last analysis poll, or null when it timed out before one arrived. */
	const finishAnalysis = (status) => {
		resumeUuid.value = status?.resume_uuid ?? null;
		recommendations.value = status?.recommendations ?? null;
		step.value = status?.is_weak ? 'weak-resume' : 'qualification';
	};

	const showPreferences = () => {
		step.value = 'preferences';
	};

	/** "Upload Another Resume" from the weak-resume step returns to EN-776's upload step. */
	const requestResumeUpload = () => {
		step.value = 'upload';
	};

	const close = () => {
		step.value = null;
	};

	return {
		step,
		hadResumeOnFile,
		uploadedNewResume,
		resumeUuid,
		recommendations,
		startAnalysis,
		finishAnalysis,
		showPreferences,
		requestResumeUpload,
		close,
	};
});
